// The operators' page (/admin, GIOTHUB-186): every organisation of the
// builder, a new organisation with its first admin's invitation, an admin
// invited again, building switched off or on. Operators = admins and
// builders of GIOTHUB_OPERATOR_ORG (Gimasi SA); this stands in for the
// console's Builder tab, which does the same through /admin/v1. Members
// are managed by each organisation's admins (Members page).
import { useEffect, useState } from "preact/hooks";
import { api } from "../lib/api";
import type { InviteResult, OperatorOverview } from "../lib/types";
import { Centered } from "../App";
import { fmtDateTime, lang, LANG_NAMES, LANGS, t, useLang, type Lang } from "../i18n";
import { useMe } from "./Projects";
import { InviteLink } from "./Members";
import { roleName, Shell } from "./Shell";

export function Operator() {
  useLang();
  const [me, meErr] = useMe();
  const [ov, setOv] = useState<OperatorOverview | null>(null);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState<{ org: string; r: InviteResult } | null>(null);
  const [form, setForm] = useState<{ name: string; first_admin_email: string; lang: Lang }>({ name: "", first_admin_email: "", lang: lang() });
  const load = () => api<OperatorOverview>("GET", "/operator/orgs").then(setOv);
  useEffect(() => {
    load().catch((e) => setErr(e.message));
  }, []);
  if (meErr) return <Centered>{meErr}</Centered>;
  if (!me || (!ov && !err)) return <Centered>{t("common.loading")}</Centered>;
  const run = async (f: () => Promise<unknown>) => {
    setErr("");
    try {
      await f();
      await load();
    } catch (x) {
      setErr((x as Error).message);
    }
  };
  const create = (e: Event) => {
    e.preventDefault();
    run(async () => {
      const r = await api<InviteResult & { organisation: { id: string } }>("POST", "/operator/orgs", form);
      setSent({ org: r.organisation.id, r });
      setForm({ ...form, name: "", first_admin_email: "" });
    });
  };
  const reinvite = (org: string) => {
    const email = prompt(t("ops.reinvite.prompt"));
    if (!email) return;
    run(async () => setSent({ org, r: await api<InviteResult>("POST", `/operator/orgs/${encodeURIComponent(org)}/invitations`, { email, role: "admin", lang: lang() }) }));
  };
  return (
    <Shell me={me} active="admin">
      <main class="mx-auto grid w-full max-w-[1200px] flex-1 content-start gap-6 overflow-y-auto p-4 sm:p-8">
        <div>
          <h1 class="mb-1 text-[20px] font-semibold">{t("ops.title")}</h1>
          <p class="text-[13px] text-muted">{t("ops.text")}</p>
        </div>
        {err && <p class="rounded-lg border border-crit bg-crit-soft px-3 py-2 text-crit" role="alert">{err}</p>}
        <div class="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <section class="grid content-start gap-3">
            {(ov?.organisations ?? []).map((o) => (
              <article class="rounded-xl border border-line bg-side p-4">
                <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <h2 class="text-[15px] font-semibold">{o.name || o.id}</h2>
                  {o.id === ov?.operator_org && <span class="chip">{t("ops.operator")}</span>}
                  <span class={`chip ${o.build_enabled ? "" : "!text-crit"}`}>{o.build_enabled ? t("ops.on") : t("ops.off")}</span>
                  <span class="text-[12px] text-muted">{t("ops.usage", { projects: o.usage?.projects ?? 0 })}{o.usage?.last_activity ? " · " + fmtDateTime(o.usage.last_activity) : ""}</span>
                  <span class="flex-1" />
                  <button type="button" class="btn btn-ghost" onClick={() => reinvite(o.id)}>{t("ops.reinvite")}</button>
                  {o.id !== ov?.operator_org && (
                    <button type="button" class="btn btn-ghost" onClick={() => run(() => api("PATCH", `/operator/orgs/${encodeURIComponent(o.id)}`, { enabled: !o.build_enabled }))}>
                      {o.build_enabled ? t("ops.switchoff") : t("ops.switchon")}
                    </button>
                  )}
                </div>
                <ul class="mt-2 grid gap-0.5 text-[12.5px]">
                  {o.members.map((m) => (
                    <li><span class="font-medium">{m.name || m.email || m.user_id}</span>{m.name && m.email && <span class="text-muted"> · {m.email}</span>} · {roleName(m.role)}</li>
                  ))}
                  {o.members.length === 0 && <li class="text-muted">{t("ops.nomembers")}</li>}
                  {o.invitations.map((i) => (
                    <li class="text-muted">{t("ops.invited", { email: i.email, role: roleName(i.role), date: fmtDateTime(i.expires_at) })}</li>
                  ))}
                </ul>
                {sent && sent.org === o.id && <InviteLink r={sent.r} />}
              </article>
            ))}
          </section>
          <form class="h-fit rounded-xl border border-line bg-side p-5" onSubmit={create}>
            <h2 class="mb-1 text-[15px] font-semibold">{t("ops.new")}</h2>
            <p class="mb-3 text-[12.5px] text-muted">{t("ops.new.text")}</p>
            <label class="mb-1 block text-[12px] font-semibold text-ink2" for="org-name">{t("ops.name")}</label>
            <input id="org-name" class="field mb-3" required value={form.name} onInput={(e) => setForm({ ...form, name: (e.target as HTMLInputElement).value })} />
            <label class="mb-1 block text-[12px] font-semibold text-ink2" for="org-admin">{t("ops.admin")}</label>
            <input id="org-admin" class="field mb-3" type="email" required value={form.first_admin_email} placeholder="name@company.com"
              onInput={(e) => setForm({ ...form, first_admin_email: (e.target as HTMLInputElement).value })} />
            <label class="mb-4 block text-[12px] font-semibold text-ink2">{t("members.lang")}
              <select class="field mt-1" value={form.lang} onChange={(e) => setForm({ ...form, lang: (e.target as HTMLSelectElement).value as Lang })}>
                {LANGS.map((l) => <option value={l}>{LANG_NAMES[l]}</option>)}
              </select>
            </label>
            <button type="submit" class="btn btn-pri w-full justify-center">{t("ops.create")}</button>
          </form>
        </div>
      </main>
    </Shell>
  );
}
